import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import MarketingNav from "@/components/marketing/MarketingNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import LaunchWaitlistModal from "@/components/marketing/LaunchWaitlistModal";

function strengthOf(pw) {
  return {
    length: pw.length >= 12,
    upper: /[A-Z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
}

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    analytics_consent: false,
    marketing_consent: false,
    terms: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const s = strengthOf(form.password);
  const valid = s.length && s.upper && s.digit && s.symbol;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.terms) {
      setError("Please agree to the Terms of Service and Privacy Policy.");
      return;
    }
    if (!valid) {
      setError("Your password doesn't meet the requirements yet.");
      return;
    }
    setLoading(true);
    try {
      const result = await register({
        email: form.email,
        password: form.password,
        name: form.name,
        analytics_consent: form.analytics_consent,
        marketing_consent: form.marketing_consent,
        tier: "spark",
      });
      if (result.pendingReview) {
        toast.info("Account under review. Please return shortly.");
        nav("/login");
      } else {
        toast.success("Welcome to WYLO.");
        nav("/onboarding");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FC]">
      <MarketingNav />
      <div className="pt-28 pb-16 px-6 lg:px-10 flex justify-center">
        <div className="w-full max-w-md" data-testid="register-page">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-3">Start your free trial</p>
          <h1 className="font-display text-4xl tracking-tighter text-[#1A1A2E] mb-2">
            Create your WYLO account.
          </h1>
          <p className="text-sm text-[#555568] mb-8">14 days of WYLO Spark free. No credit card.</p>

          <form onSubmit={submit} className="space-y-5 bg-white border border-[#E8ECF2] rounded-xl p-7" aria-hidden="true">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="register-name" disabled tabIndex={-1} />
            </div>
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email" disabled tabIndex={-1} />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="register-password" disabled tabIndex={-1} />
              <ul className="mt-2 grid grid-cols-2 gap-1 text-xs" data-testid="password-strength">
                <Req ok={s.length} label="≥ 12 characters" />
                <Req ok={s.upper} label="1 uppercase" />
                <Req ok={s.digit} label="1 number" />
                <Req ok={s.symbol} label="1 symbol" />
              </ul>
            </div>

            <div className="space-y-3 border-t border-[#E8ECF2] pt-4">
              <p className="text-xs uppercase tracking-[0.18em] text-[#555568] font-semibold">Consent</p>
              <ConsentRow
                checked={true}
                disabled
                label="Necessary data collection"
                desc="Required to operate WYLO."
              />
              <ConsentRow
                checked={form.analytics_consent}
                onChange={(v) => setForm({ ...form, analytics_consent: v })}
                label="Analytics & usage data"
                desc="Help us improve WYLO. Aggregated and anonymised."
                testid="register-consent-analytics"
                disabled
              />
              <ConsentRow
                checked={form.marketing_consent}
                onChange={(v) => setForm({ ...form, marketing_consent: v })}
                label="Marketing communications"
                desc="Occasional product updates. Unsubscribe any time."
                testid="register-consent-marketing"
                disabled
              />
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={form.terms}
                  onCheckedChange={(v) => setForm({ ...form, terms: !!v })}
                  data-testid="register-terms"
                  disabled
                />
                <Label htmlFor="terms" className="text-sm text-[#1A1A2E] font-normal leading-relaxed">
                  I agree to the <Link to="/legal/terms" className="text-[#2C6BAC] hover:underline">Terms of Service</Link> and{" "}
                  <Link to="/legal/privacy" className="text-[#2C6BAC] hover:underline">Privacy Policy</Link>.
                </Label>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-md border border-[#E24B4A]/40 bg-[#E24B4A]/10 text-[#E24B4A] text-sm px-3 py-2"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <Button disabled className="w-full bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="register-submit" tabIndex={-1}>
              Available at launch
            </Button>

            <p className="text-sm text-[#555568] text-center">
              Already have an account? <Link to="/login" className="text-[#2C6BAC] hover:underline">Log in</Link>
            </p>
          </form>
        </div>
      </div>

      <LaunchWaitlistModal />
    </div>
  );
}

function Req({ ok, label }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? "text-[#1D9E75]" : "text-[#555568]"}`}>
      {ok ? <Check size={12} /> : <X size={12} />}
      {label}
    </li>
  );
}

function ConsentRow({ checked, onChange, label, desc, disabled, testid }) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
      <div>
        <p className="text-sm text-[#1A1A2E]">{label}</p>
        <p className="text-xs text-[#555568]">{desc}</p>
      </div>
    </div>
  );
}
